import { Dialog, DialogConfig, DialogRef } from '@angular/cdk/dialog';
import { ComponentType } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, inject, Injectable, input, TemplateRef } from '@angular/core';
import { X } from 'lucide';
import { NbIcon } from '../../icon/icon';
import { injectNbUiText } from '../../i18n/ui-text';

/**
 * `NbDialog` — opens a modal dialog over the CDK overlay. The CDK `Dialog`
 * gives us `role="dialog"` + `aria-modal`, an automatic focus trap, focus
 * restoration on close, and ESC-to-dismiss. We add the Atelier scrim + panel.
 *
 * @example
 * const ref = nbDialog.open(MyDialogComponent, { ariaLabel: 'Bekreft' });
 */
@Injectable({ providedIn: 'root' })
export class NbDialog {
  private readonly cdk = inject(Dialog);

  open<R = unknown, D = unknown, C = unknown>(
    content: ComponentType<C> | TemplateRef<C>,
    config: DialogConfig<D, DialogRef<R, C>> = {},
  ): DialogRef<R, C> {
    return this.cdk.open<R, D, C>(content, {
      backdropClass: 'nb-scrim',
      panelClass: 'nb-dialog-panel',
      hasBackdrop: true,
      ...config,
    });
  }
}

/**
 * `<nb-dialog>` — the visual shell to place inside a dialog: a titled card with
 * a close affordance, a scrollable body, and an optional footer slot
 * (`[nbDialogFooter]`). Closes the surrounding CDK dialog via the injected ref.
 */
@Component({
  selector: 'nb-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NbIcon],
  host: { '[attr.aria-labelledby]': 'titleId', '[attr.aria-describedby]': 'describedBy() || null' },
  template: `<div class="nb-dialog-card">
    <header class="nb-dialog-head">
      <h2 class="h2" [id]="titleId">{{ title() }}</h2>
      @if (dismissable()) {
        <button type="button" class="nb-dialog-close" [attr.aria-label]="closeText()" (click)="close()">
          <nb-icon [icon]="X" [size]="20" />
        </button>
      }
    </header>
    <div class="nb-dialog-body"><ng-content /></div>
    <footer class="nb-dialog-foot"><ng-content select="[nbDialogFooter]" /></footer>
  </div>`,
  styleUrl: './dialog.css',
})
export class NbDialogShell {
  /** Accessible name of the close button; defaults to the app's design-system copy. */
  readonly closeLabel = input<string | undefined>(undefined);
  private readonly uiText = injectNbUiText();
  protected readonly closeText = computed(() => this.closeLabel() ?? this.uiText().close);
  private readonly ref = inject<DialogRef<unknown, unknown>>(DialogRef, { optional: true });

  readonly title = input('');
  readonly dismissable = input(true);
  /** Id of the element that describes the dialog (e.g. the confirmation text) — sets `aria-describedby`. */
  readonly describedBy = input<string>();
  protected readonly titleId = `nb-dialog-${nextId++}`;
  protected readonly X = X;

  protected close(): void {
    this.ref?.close();
  }
}

let nextId = 0;
